import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Clock, Award, Target, BookOpen, Star, Users, Share, Download } from 'lucide-react';
import Button from '../../../components/ui/Button';

const ProgressPanel = ({ 
  tutorial, 
  lesson, 
  userProgress = {}, 
  setUserProgress, 
  currentUser 
}) => {
  const [showAchievements, setShowAchievements] = useState(false);

  const markLessonComplete = () => {
    if (!currentUser || !lesson) return;
    
    const completedLessons = userProgress?.completedLessons || [];
    if (!completedLessons?.includes(lesson?.id)) {
      const updatedProgress = {
        ...userProgress,
        completedLessons: [...completedLessons, lesson?.id],
        lastAccessed: new Date()?.toISOString()
      };
      
      setUserProgress(prev => ({
        ...prev,
        [tutorial?.id]: updatedProgress
      }));
      
      // Save to localStorage
      localStorage.setItem(
        `progress_${currentUser?.id}`, 
        JSON.stringify({
          ...JSON.parse(localStorage.getItem(`progress_${currentUser?.id}`) || '{}'),
          [tutorial?.id]: updatedProgress
        })
      );
    }
  };

  const totalLessons = tutorial?.chapters?.reduce((total, chapter) => total + chapter?.lessons?.length, 0) || 0;
  const completedCount = userProgress?.completedLessons?.length || 0;
  const progressPercentage = totalLessons > 0 ? (completedCount / totalLessons) * 100 : 0;
  
  const isLessonCompleted = lesson && userProgress?.completedLessons?.includes(lesson?.id);
  
  // Mock achievements data
  const achievements = [
    { id: 1, name: 'First Step', description: 'Complete your first lesson', icon: '🎯', earned: completedCount > 0 },
    { id: 2, name: 'Quick Learner', description: 'Complete 5 lessons', icon: '⚡', earned: completedCount >= 5 },
    { id: 3, name: 'Dedicated Student', description: 'Complete 50% of tutorial', icon: '📚', earned: progressPercentage >= 50 },
    { id: 4, name: 'Master Graduate', description: 'Complete entire tutorial', icon: '🏆', earned: progressPercentage >= 100 }
  ];

  const earnedAchievements = achievements?.filter(a => a?.earned);
  const nextAchievement = achievements?.find(a => !a?.earned);

  if (!tutorial) return null;

  return (
    <div className="h-full bg-card border-l border-border flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <h3 className="font-semibold mb-2">Learning Progress</h3>
        <p className="text-sm text-muted-foreground">
          Track your progress and achievements
        </p>
      </div>
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Overall Progress */}
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="font-medium">Tutorial Progress</span>
              <span className="text-blue-600 font-semibold">{Math.round(progressPercentage)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <motion.div 
                className="bg-blue-500 h-3 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progressPercentage}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {completedCount} of {totalLessons} lessons completed
            </div>
          </div>

          {/* Current Lesson Status */}
          {lesson && (
            <div className="bg-muted/20 rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  isLessonCompleted ? 'bg-green-500' : 'bg-blue-500'
                }`}>
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-sm">{lesson?.title}</h4>
                  <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                    <Clock className="w-3 h-3" />
                    <span>{lesson?.duration}</span>
                    <span>•</span>
                    <span className="capitalize">{lesson?.type}</span>
                  </div>
                </div>
              </div>
              
              {currentUser && !isLessonCompleted && (
                <Button
                  size="sm"
                  onClick={markLessonComplete}
                  className="w-full"
                >
                  Mark as Complete
                </Button>
              )}
              
              {isLessonCompleted && (
                <div className="flex items-center space-x-2 text-green-600 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  <span>Completed</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Achievements Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold flex items-center space-x-2">
              <Award className="w-4 h-4" />
              <span>Achievements</span>
            </h4>
            <button
              onClick={() => setShowAchievements(!showAchievements)}
              className="text-sm text-blue-600 hover:underline"
            >
              {showAchievements ? 'Hide' : 'Show All'}
            </button>
          </div>

          {/* Latest Achievement */}
          {earnedAchievements?.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <span className="text-xl">{earnedAchievements?.[earnedAchievements?.length - 1]?.icon}</span>
                <div>
                  <div className="font-medium text-sm text-yellow-800">
                    {earnedAchievements?.[earnedAchievements?.length - 1]?.name}
                  </div>
                  <div className="text-xs text-yellow-600">
                    {earnedAchievements?.[earnedAchievements?.length - 1]?.description}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Next Achievement Goal */}
          {nextAchievement && (
            <div className="bg-muted/20 border border-border rounded-lg p-3">
              <div className="flex items-center space-x-2 opacity-60">
                <span className="text-xl">{nextAchievement?.icon}</span>
                <div>
                  <div className="font-medium text-sm">
                    {nextAchievement?.name}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {nextAchievement?.description}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* All Achievements */}
          {showAchievements && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="space-y-2"
            >
              {achievements?.map(achievement => (
                <div
                  key={achievement?.id}
                  className={`flex items-center space-x-2 p-2 rounded ${
                    achievement?.earned ? 'bg-green-50' : 'bg-muted/20 opacity-40'
                  }`}
                >
                  <span className="text-lg">{achievement?.icon}</span>
                  <div>
                    <div className="font-medium text-xs">{achievement?.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {achievement?.description}
                    </div>
                  </div>
                  {achievement?.earned && (
                    <CheckCircle className="w-4 h-4 text-green-500 ml-auto" />
                  )}
                </div>
              ))}
            </motion.div>
          )}
        </div>

        {/* Tutorial Stats */}
        <div className="space-y-4">
          <h4 className="font-semibold">Tutorial Stats</h4>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="text-center p-3 bg-muted/20 rounded-lg">
              <div className="text-lg font-bold text-blue-600">{tutorial?.rating}</div>
              <div className="text-xs text-muted-foreground flex items-center justify-center space-x-1">
                <Star className="w-3 h-3" />
                <span>Rating</span>
              </div>
            </div>
            
            <div className="text-center p-3 bg-muted/20 rounded-lg">
              <div className="text-lg font-bold text-green-600">{tutorial?.enrolled?.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground flex items-center justify-center space-x-1">
                <Users className="w-3 h-3" />
                <span>Students</span>
              </div>
            </div>
            
            <div className="text-center p-3 bg-muted/20 rounded-lg">
              <div className="text-lg font-bold text-purple-600">{tutorial?.lessons}</div>
              <div className="text-xs text-muted-foreground flex items-center justify-center space-x-1">
                <BookOpen className="w-3 h-3" />
                <span>Lessons</span>
              </div>
            </div>
            
            <div className="text-center p-3 bg-muted/20 rounded-lg">
              <div className="text-lg font-bold text-orange-600">{tutorial?.estimatedTime}</div>
              <div className="text-xs text-muted-foreground flex items-center justify-center space-x-1">
                <Clock className="w-3 h-3" />
                <span>Duration</span>
              </div>
            </div>
          </div>
        </div>

        {/* Learning Path */}
        <div className="space-y-4">
          <h4 className="font-semibold">Learning Objectives</h4>
          
          {tutorial?.learningObjectives?.map((objective, index) => (
            <div key={index} className="flex items-start space-x-2">
              <Target className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <span className="text-sm">{objective}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Footer Actions */}
      <div className="p-4 border-t border-border space-y-2">
        <Button
          variant="outline"
          size="sm"
          className="w-full flex items-center justify-center space-x-2"
        >
          <Download className="w-4 h-4" />
          <span>Download Resources</span>
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          className="w-full flex items-center justify-center space-x-2"
        >
          <Share className="w-4 h-4" />
          <span>Share Progress</span>
        </Button>
      </div>
    </div>
  );
};

export default ProgressPanel;